import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { TEMPLATES } from "@/pages/admin/newsletterTemplates";

const csv = (rows) => {
  const lines = [["email", "status", "welcome_code_used", "joined"], ...rows.map((s) => [s.email, s.status, s.welcome_used ? "yes" : "no", s.created_at])];
  const blob = new Blob([lines.map((l) => l.join(",")).join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "albion-pantry-subscribers.csv";
  a.click();
};

function Compose({ count, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm(`Send "${subject}" to ${count} subscriber${count === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter", { subject, body, origin_url: window.location.origin });
      toast.success(`Sending to ${data.recipients} subscribers`);
      setSubject(""); setBody(""); onSent(); setTimeout(onSent, 4000); setTimeout(onSent, 15000);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl bg-white border border-[#EDE4D3] p-5" data-testid="newsletter-compose">
      <h3 className="font-serif text-xl font-semibold">Write a newsletter</h3>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="newsletter-templates">
        <span className="text-xs text-slate-500 self-center">Start from a template:</span>
        {TEMPLATES.map((t) => (
          <button key={t.key} type="button" onClick={() => { if ((subject || body) && !window.confirm("Replace what you've written with this template?")) return; setSubject(t.subject); setBody(t.body); }} className="rounded-full border border-[#E2D7C3] px-3 py-1 text-xs hover:border-[#8C2220] hover:text-[#8C2220] transition-colors" data-testid={`newsletter-template-${t.key}`}>{t.label}</button>
        ))}
      </div>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject, e.g. Our Easter shop is open" className="mt-3 w-full rounded-xl border px-4 py-2.5 text-sm" data-testid="newsletter-subject-input" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} placeholder={"Write your message. Leave a blank line between paragraphs."} className="mt-3 w-full rounded-xl border px-4 py-2.5 text-sm" data-testid="newsletter-body-input" />
      <p className="text-xs text-slate-500">Your logo, a "Visit the shop" button and an unsubscribe link are added automatically.</p>
      <button disabled={busy || subject.trim().length < 3 || body.trim().length < 10 || !count} onClick={send} className="mt-3 rounded-full bg-[#8C2220] text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-40" data-testid="newsletter-send-button">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to {count} subscriber{count === 1 ? "" : "s"}
      </button>
    </div>
  );
}

export default function NewsletterTab() {
  const [subs, setSubs] = useState([]);
  const [sent, setSent] = useState([]);
  const load = () => {
    api.get("/admin/subscribers").then((r) => setSubs(r.data));
    api.get("/admin/newsletters").then((r) => setSent(r.data));
  };
  useEffect(() => { load(); }, []);
  const active = subs.filter((s) => s.status === "active");

  return (
    <div className="mt-4 grid lg:grid-cols-2 gap-6" data-testid="newsletter-tab">
      <div className="space-y-6">
        <Compose count={active.length} onSent={load} />
        {sent.length > 0 && (
          <div className="rounded-2xl bg-white border border-[#EDE4D3] p-5" data-testid="newsletter-history">
            <h3 className="font-semibold">Sent newsletters</h3>
            {sent.map((n) => <p key={n.id} className="mt-2 text-sm flex justify-between gap-3"><span>{n.subject}</span><span className="text-slate-500 text-xs">{n.status === "sending" ? "Sending…" : `${n.sent}/${n.recipients} sent`} · {new Date(n.created_at).toLocaleDateString("en-GB")}</span></p>)}
          </div>
        )}
      </div>
      <div className="rounded-2xl bg-white border border-[#EDE4D3] p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold" data-testid="subscriber-count">{active.length} active subscriber{active.length === 1 ? "" : "s"}</h3>
          <button disabled={!subs.length} onClick={() => csv(subs)} className="rounded-full border px-4 py-1.5 text-sm flex items-center gap-2 disabled:opacity-40" data-testid="subscribers-export-button"><Download className="w-4 h-4" /> Export CSV</button>
        </div>
        <div className="mt-3 divide-y divide-[#EDE4D3] max-h-[480px] overflow-auto" data-testid="subscriber-list">
          {!subs.length && <p className="text-sm text-slate-500 py-3">No subscribers yet — the signup box is in the site footer.</p>}
          {subs.map((s) => (
            <div key={s.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`subscriber-${s.email}`}>
              <span className="flex-1 truncate">{s.email}</span>
              {s.welcome_used && <span className="text-xs text-emerald-700">used WELCOME10</span>}
              <span className={`text-xs ${s.status === "active" ? "text-emerald-700" : "text-slate-400"}`}>{s.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
